import { useScroll, useVirtualList } from '@vueuse/core';
import { computed, nextTick, type Ref, ref, watch } from 'vue';

export interface InfiniteScrollOptions<T = any> {
  /** The API function to call for fetching data */
  fetchFunction: (cursor?: string) => Promise<{
    data: T[];
    next_cursor: string | undefined;
    prev_cursor: string | undefined;
    has_more: boolean;
  }>;
  /** Initial cursor value */
  initialCursor?: string;
  /** Whether to load data immediately on mount */
  immediate?: boolean;
  /** Prefetch distance from the scroll edge in px */
  edgeOffset?: number;
  /** Row height in px, or a function of index */
  itemHeight?: number | ((index: number) => number);
  /** Extra buffer rows outside the viewport */
  overscan?: number;
}

export function useInfiniteScroll<T = any>(options: InfiniteScrollOptions<T>) {
  const {
    fetchFunction,
    initialCursor,
    immediate = true,
    edgeOffset = 200,
    itemHeight = 64,
    overscan = 10,
  } = options;

  const items = ref<T[]>([]) as Ref<T[]>;
  const loadingMore = ref(false);
  const loadingPrev = ref(false);
  const loading = computed(() => loadingMore.value || loadingPrev.value);
  const hasMore = ref(true);
  const hasPrev = ref(false);
  const nextCursor = ref<string | undefined>(initialCursor);
  const prevCursor = ref<string | undefined>(undefined);
  const firstLoad = ref(false);

  const {
    list: virtualList,
    containerProps,
    wrapperProps,
    scrollTo,
  } = useVirtualList(items, {
    itemHeight,
    overscan,
  });

  const scrollContainerRef = containerProps.ref;

  const resolveItemHeight = (index: number) => {
    return typeof itemHeight === 'function' ? itemHeight(index) : itemHeight;
  };

  const restoreScrollByPrependedHeight = async (prependedCount: number) => {
    await nextTick();

    const el = scrollContainerRef.value;

    if(!el || prependedCount <= 0) return;

    let addedHeight = 0;

    for(let i = 0;i < prependedCount;i++) {
      addedHeight += resolveItemHeight(i);
    }

    el.scrollTop += addedHeight;
  };

  const isNearBottom = () => {
    const el = scrollContainerRef.value;

    if(!el) return false;

    return el.scrollHeight - el.scrollTop - el.clientHeight <= edgeOffset;
  };

  const isNearTop = () => {
    const el = scrollContainerRef.value;

    if(!el) return false;

    return el.scrollTop <= edgeOffset;
  };

  const loadMore = async () => {
    if(loading.value || !hasMore.value) return;

    loadingMore.value = true;

    try {
      const result = await fetchFunction(nextCursor.value);

      items.value = items.value.concat(result.data);
      nextCursor.value = result.next_cursor;
      hasMore.value = result.has_more;

      if(!firstLoad.value) {
        prevCursor.value = result.prev_cursor;
        hasPrev.value = !!result.prev_cursor;
      }

      firstLoad.value = true;
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      loadingMore.value = false;

      await nextTick();

      if(isNearBottom() && hasMore.value) {
        loadMore();
      }
    }
  };

  const loadPrev = async () => {
    if(loading.value || !prevCursor.value) return;

    loadingPrev.value = true;

    try {
      const result = await fetchFunction(prevCursor.value);
      const prependedCount = result.data.length;

      items.value = result.data.concat(items.value);
      prevCursor.value = result.prev_cursor;
      hasPrev.value = !!result.prev_cursor;

      await restoreScrollByPrependedHeight(prependedCount);
    } catch (error) {
      console.error('Error loading prev items:', error);
    } finally {
      loadingPrev.value = false;

      await nextTick();

      if(isNearTop() && hasPrev.value) {
        loadPrev();
      }
    }
  };

  const reset = () => {
    loadingMore.value = false;
    loadingPrev.value = false;
    hasMore.value = true;
    hasPrev.value = false;
    nextCursor.value = initialCursor;
    prevCursor.value = undefined;
    items.value = [];
    firstLoad.value = false;

    nextTick(() => {
      scrollContainerRef.value?.scrollTo({ top: 0 });
    });
  };

  const { arrivedState } = useScroll(scrollContainerRef, {
    offset: { top: edgeOffset, bottom: edgeOffset },
  });

  watch(
    () => arrivedState.bottom,
    (arrived) => {
      if(arrived) {
        loadMore();
      }
    },
  );

  watch(
    () => arrivedState.top,
    (arrived) => {
      if(arrived) {
        loadPrev();
      }
    },
  );

  if(immediate) {
    loadMore();
  }

  return {
    items,
    loading,
    loadingMore,
    loadingPrev,
    hasMore,
    hasPrev,
    loadMore,
    loadPrev,
    reset,
    firstLoad,
    scrollContainerRef,
    virtualList,
    containerProps,
    wrapperProps,
    scrollTo,
  };
}
